import { mapToKey } from '@witmetrics/utils/arrays';
import {
  useDispatch,
  useNotesFromFileId,
  usePracticeUsers,
} from '@witmetrics/data-store';
import { isEmpty } from '@witmetrics/utils/misc';
import { useAppState } from '@/providers/AppStateProvider';
import { useToggle } from '@/hooks/useToggle';
import { showErrorToaster } from '@/utils/toasters';
import NotesField from '@/components/inputs/NotesField';
import NotesTimeline from '@/components/NotesTimeline';
import { Tab, type TabProps } from '@/components/TabsBlock';
import Title from '@/components/Title';
import Button from '@/components/Button';
import { classes, createFileNote } from './utils';

export interface NotesTabProps extends TabProps {
  activeFileId?: number;
}

export default function NotesTab({ name, label, activeFileId }: NotesTabProps) {
  const dispatch = useDispatch();
  const { onApiError } = useAppState();
  const notes = useNotesFromFileId(activeFileId);
  const practiceUsers = usePracticeUsers();
  const [isNewNoteShown, toggleNewNote] = useToggle();
  const [isSavingNote, toggleSavingNote] = useToggle();

  const handleSaveNote = async (content: string) => {
    try {
      if (!activeFileId) {
        console.warn('Missing activeFileId');
        return showErrorToaster('Error saving note');
      }
      toggleSavingNote(true);
      dispatch.addFileNotes([await createFileNote(activeFileId, content)]);
      toggleSavingNote(false);
      toggleNewNote(false);
    } catch (err) {
      toggleSavingNote(false);
      onApiError(err, 'Error saving note');
    }
  };

  if (!activeFileId) {
    return (
      <Tab name={name} label={label}>
        <div className={classes.message}>No active file.</div>
      </Tab>
    );
  }

  return (
    <Tab name={name} label={label}>
      <div className={classes.titleWrapper}>
        <Title size="xs">Notes</Title>
        <Button
          className={classes.addNoteButton}
          variant="ghost"
          onClick={() => toggleNewNote(true)}>
          Add Note
        </Button>
      </div>
      {isNewNoteShown && (
        <div className={classes.newNoteWrapper}>
          <NotesField
            isSaving={isSavingNote}
            users={mapToKey(practiceUsers, 'user')}
            onSave={handleSaveNote}
            onCancel={() => toggleNewNote(false)}
          />
        </div>
      )}
      {isEmpty(notes) ? (
        <div className={classes.message}>No notes for current file.</div>
      ) : (
        <NotesTimeline
          className={classes.timeline}
          variant="aurora"
          notes={notes}
        />
      )}
    </Tab>
  );
}
